<template>
  <div class="d-inline-block">
    <v-menu
      v-model="menuActive"
      :bottom="menuPosition === 'bottom'"
      :left="menuPosition === 'left'"
      :min-width="300"
      :nudge-width="300"
      :offset-x="menuOffsetX"
      :offset-y="menuOffsetY"
      :open-on-hover="openOnHover"
      :right="menuPosition === 'right'"
      :top="menuPosition === 'top'"
    >
      <template #activator="{on: onMenu}">
        <v-tooltip
          :bottom="tooltipPosition === 'bottom'"
          :close-delay="tooltipCloseDelay"
          :color="color"
          :disabled="disabled || disableTooltip || openOnHover"
          :left="tooltipPosition === 'left'"
          :open-delay="tooltipOpenDelay"
          :right="tooltipPosition === 'right'"
          :top="tooltipPosition === 'top'"
        >
          <template #activator="{on: onTooltip}">
            <v-btn
              :absolute="absolute"
              :block="block"
              :class="[btnClass, icon ? color : '']"
              :color="icon ? '' : color"
              :dark="dark"
              :depressed="depressed"
              :disabled="disabled || (!ordersReportActive && !receiptsReportActive && !productsReportActive)"
              :fab="fab"
              :fixed="fixed"
              :icon="icon"
              :large="large"
              :outlined="outline"
              :small="small"
              :style="btnStyle"
              :text="flat"
              v-on="{...onMenu, ...onTooltip}"
            >
              <v-icon
                v-if="!hideIcon"
                :left="!!label && !hideLabel && !icon"
                :small="small"
              >
                {{ iconName }}
              </v-icon>
              <span v-if="label && !hideLabel && !icon">{{ label }}</span>
            </v-btn>
          </template>
          <span>{{ tooltip }}</span>
        </v-tooltip>
      </template>

      <v-list class="pa-1">
        <template v-if="ordersReportActive">
          <v-subheader class="info lighten-1 white--text font-weight-bold body-1 rounded">
            <v-icon
              class="mr-2"
              dark
            >
              mdi-file-chart-outline
            </v-icon>
            {{ $t('Statistics.SalesReportOrders.Title') }}
          </v-subheader>
          <template v-if="machineHasPrintersForOrdersReport && ordersReportPrint">
            <template v-if="machineHasMultiplePrintersForOrdersReport">
              <v-menu
                v-model="menuPrintOrdersReportActive"
                :min-width="300"
                nudge-right="8"
                offset-x
              >
                <template #activator="{on}">
                  <v-list-item v-on="on">
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
                  </v-list-item>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(printer, index) in appMachinePrintersActive"
                    :key="`orders-printer-${index}`"
                    @click="onPrintOrdersReportClick(printer)"
                  >
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ printer.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-else>
              <v-list-item @click="onPrintOrdersReportClick(appMachinePrintersActive[0])">
                <v-list-item-action>
                  <v-icon>print</v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
              </v-list-item>
            </template>
          </template>

          <template v-if="ordersReportPdf">
            <v-list-item @click="onDownloadPdfOrdersReportClick">
              <v-list-item-action>
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.Download') }} PDF</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="ordersReportEmail">
            <v-list-item @click="onSendEmailOrdersReportClick">
              <v-list-item-action>
                <v-icon>mdi-email-arrow-right</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.EmailSendPdf') }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="ordersReportHistory">
            <v-list-item @click="onHistoryOrdersReportClick">
              <v-list-item-action>
                <v-icon>mdi-clipboard-text-clock-outline</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.ReportHistory') }}</v-list-item-title>
            </v-list-item>
          </template>
        </template>

        <template v-if="receiptsReportActive">
          <v-subheader class="orange lighten-1 white--text font-weight-bold body-1 rounded">
            <v-icon
              class="mr-2"
              dark
            >
              mdi-file-chart-outline
            </v-icon>
            {{ $t('Reports.SalesReportReceiptsZ.Title') }}
          </v-subheader>
          <template v-if="machineHasPrintersForReceiptsReport && receiptsReportPrint">
            <template v-if="machineHasMultiplePrintersForReceiptsReport">
              <v-menu
                v-model="menuPrintReceiptsReportActive"
                :min-width="300"
                nudge-right="8"
                offset-x
              >
                <template #activator="{on}">
                  <v-list-item v-on="on">
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
                  </v-list-item>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(printer, index) in appMachineFiscalDocTotalsZPrintersActive"
                    :key="`receipts-printer-${index}`"
                    @click="onPrintReceiptsReportClick(printer)"
                  >
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ printer.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-else>
              <v-list-item @click="onPrintReceiptsReportClick(appMachineFiscalDocTotalsZPrintersActive[0])">
                <v-list-item-action>
                  <v-icon>print</v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
              </v-list-item>
            </template>
          </template>

          <template v-if="receiptsReportPdf">
            <v-list-item @click="onDownloadPdfReceiptsReportClick">
              <v-list-item-action>
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.Download') }} PDF</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="receiptsReportEmail">
            <v-list-item @click="onSendEmailReceiptsReportClick">
              <v-list-item-action>
                <v-icon>mdi-email-arrow-right</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.EmailSendPdf') }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="receiptsReportHistory">
            <v-list-item @click="onHistoryReceiptsReportClick">
              <v-list-item-action>
                <v-icon>mdi-clipboard-text-clock-outline</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.ReportHistory') }}</v-list-item-title>
            </v-list-item>
          </template>
        </template>

        <template v-if="productsReportActive">
          <v-subheader class="green lighten-1 white--text font-weight-bold body-1 rounded">
            <v-icon
              class="mr-2"
              dark
            >
              mdi-file-chart-outline
            </v-icon>
            {{ $t('Statistics.Product.Title') }}
          </v-subheader>
          <template v-if="machineHasPrintersForProductsReport && productsReportPrint">
            <template v-if="machineHasMultiplePrintersForProductsReport">
              <v-menu
                v-model="menuPrintProductsReportActive"
                :min-width="300"
                nudge-right="8"
                offset-x
              >
                <template #activator="{on}">
                  <v-list-item v-on="on">
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
                  </v-list-item>
                </template>

                <v-list>
                  <v-list-item
                    v-for="(printer, index) in appMachinePrintersActive"
                    :key="`orders-printer-${index}`"
                    @click="onPrintProductsReportClick(printer)"
                  >
                    <v-list-item-action>
                      <v-icon>print</v-icon>
                    </v-list-item-action>
                    <v-list-item-title>{{ printer.name }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
            <template v-else>
              <v-list-item @click="onPrintProductsReportClick(appMachinePrintersActive[0])">
                <v-list-item-action>
                  <v-icon>print</v-icon>
                </v-list-item-action>
                <v-list-item-title>{{ $t('Common.Button.Print') }}</v-list-item-title>
              </v-list-item>
            </template>
          </template>

          <template v-if="productsReportPdf">
            <v-list-item @click="onDownloadPdfProductsReportClick">
              <v-list-item-action>
                <v-icon>mdi-file-pdf-box</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.Download') }} PDF</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="productsReportEmail">
            <v-list-item @click="onSendEmailProductsReportClick">
              <v-list-item-action>
                <v-icon>mdi-email-arrow-right</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.EmailSendExcel') }}</v-list-item-title>
            </v-list-item>
          </template>

          <template v-if="productsReportHistory">
            <v-list-item @click="onHistoryProductsReportClick">
              <v-list-item-action>
                <v-icon>mdi-clipboard-text-clock-outline</v-icon>
              </v-list-item-action>
              <v-list-item-title>{{ $t('Common.Button.ReportHistory') }}</v-list-item-title>
            </v-list-item>
          </template>
        </template>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'

export default {
  name      : 'ReportsButton',
  components: {},
  directives: {},
  mixins    : [Auth, AppData],
  props     : {
    ordersReport: {
      type   : Boolean,
      default: false
    },
    ordersReportPrint: {
      type   : Boolean,
      default: false
    },
    ordersReportPdf: {
      type   : Boolean,
      default: false
    },
    ordersReportEmail: {
      type   : Boolean,
      default: false
    },
    ordersReportHistory: {
      type   : Boolean,
      default: false
    },

    receiptsReport: {
      type   : Boolean,
      default: false
    },
    receiptsReportPrint: {
      type   : Boolean,
      default: false
    },
    receiptsReportPdf: {
      type   : Boolean,
      default: false
    },
    receiptsReportEmail: {
      type   : Boolean,
      default: false
    },
    receiptsReportHistory: {
      type   : Boolean,
      default: false
    },

    productsReport: {
      type   : Boolean,
      default: false
    },
    productsReportPrint: {
      type   : Boolean,
      default: false
    },
    productsReportPdf: {
      type   : Boolean,
      default: false
    },
    productsReportEmail: {
      type   : Boolean,
      default: false
    },
    productsReportHistory: {
      type   : Boolean,
      default: false
    },
    tooltip: {
      type   : String,
      default: function () {
        return this.$t('Common.Button.Reports')
      }
    },
    label: {
      type   : String,
      default: function () {
        return this.$t('Common.Button.Reports').toLocaleUpperCase(this.$i18n.locale)
      }
    },
    hideLabel: {
      type   : Boolean,
      default: false
    },
    btnClass: {
      type   : String,
      default: 'white--text'
    },
    btnStyle: {
      type   : String,
      default: ''
    },
    color: {
      type   : String,
      default: 'purple lighten-2'
    },
    icon: {
      type   : Boolean,
      default: false
    },
    iconName: {
      type   : String,
      default: 'mdi-file-chart-outline'
    },
    hideIcon: {
      type   : Boolean,
      default: false
    },
    small: {
      type   : Boolean,
      default: false
    },
    large: {
      type   : Boolean,
      default: false
    },
    outline: {
      type   : Boolean,
      default: false
    },
    flat: {
      type   : Boolean,
      default: false
    },
    depressed: {
      type   : Boolean,
      default: false
    },
    absolute: {
      type   : Boolean,
      default: false
    },
    disabled: {
      type   : Boolean,
      default: false
    },
    block: {
      type   : Boolean,
      default: false
    },
    fixed: {
      type   : Boolean,
      default: false
    },
    fab: {
      type   : Boolean,
      default: false
    },
    dark: {
      type   : Boolean,
      default: false
    },
    menuPosition: {
      type   : String,
      default: 'bottom'
    },
    menuOffsetY: {
      type   : Boolean,
      default: true
    },
    menuOffsetX: {
      type   : Boolean,
      default: false
    },
    tooltipPosition: {
      type   : String,
      default: 'top'
    },
    tooltipOpenDelay: {
      type   : Number,
      default: 1200
    },
    tooltipCloseDelay: {
      type   : Number,
      default: 200
    },
    disableTooltip: {
      type   : Boolean,
      default: false
    },
    openOnHover: {
      type   : Boolean,
      default: false
    }
  },
  dataStore: {},
  data() {
    return {
      menuActive                   : false,
      menuPrintOrdersReportActive  : false,
      menuPrintProductsReportActive: false,
      menuPrintReceiptsReportActive: false
    }
  },
  computed: {
    machineHasPrintersForOrdersReport() {
      return this.appMachinePrintersActive.length && this.appHasPrinters
    },
    machineHasMultiplePrintersForOrdersReport() {
      return this.machineHasPrintersForOrdersReport && this.appMachinePrintersActive.length > 1
    },

    machineHasPrintersForReceiptsReport() {
      return this.appMachineFiscalDocTotalsZPrintersActive.length && this.appHasFiscalDocTotalsZPrinters
    },
    machineHasMultiplePrintersForReceiptsReport() {
      return this.appMachineHasFiscalDocTotalsZPrinters && this.appMachineFiscalDocTotalsZPrintersActive.length > 1
    },

    machineHasPrintersForProductsReport() {
      return this.appMachinePrintersActive.length && this.appHasPrinters
    },
    machineHasMultiplePrintersForProductsReport() {
      return this.machineHasPrintersForProductsReport && this.appMachinePrintersActive.length > 1
    },

    ordersReportActive() {
      return this.ordersReport && (this.ordersReportPrint || this.ordersReportEmail || this.ordersReportPdf)
    },

    receiptsReportActive() {
      return this.receiptsReport && (this.receiptsReportPrint || this.receiptsReportEmail || this.receiptsReportPdf)
    },

    productsReportActive() {
      return this.productsReport && (this.productsReportPrint || this.productsReportEmail || this.productsReportPdf)
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {
    hideMenus() {
      this.menuActive = false
      this.menuPrintOrdersReportActive = false
      this.menuPrintProductsReportActive = false
      this.menuPrintReceiptsReportActive = false
    },

    onPrintOrdersReportClick(printer) {
      if (!printer) return

      this.hideMenus()
      this.$emit('print-orders-report', printer)
    },
    onDownloadPdfOrdersReportClick() {
      this.$emit('download-pdf-orders-report')
    },
    onSendEmailOrdersReportClick() {
      this.$emit('send-email-orders-report')
    },
    onHistoryOrdersReportClick() {
      this.$emit('history-orders-report')
      this.$router.push({name: 'ReportsNotify', params: {filterReportType: 'orders_all'}})
    },

    onPrintReceiptsReportClick(printer) {
      if (!printer) return

      this.hideMenus()
      this.$emit('print-receipts-report', printer)
    },
    onDownloadPdfReceiptsReportClick() {
      this.$emit('download-pdf-receipts-report')
    },
    onSendEmailReceiptsReportClick() {
      this.$emit('send-email-receipts-report')
    },
    onHistoryReceiptsReportClick() {
      this.$emit('history-receipts-report')
      this.$router.push({name: 'ReportsNotify', params: {filterReportType: 'receipts_all'}})
    },

    onPrintProductsReportClick(printer) {
      if (!printer) return

      this.hideMenus()
      this.$emit('print-products-report', printer)
    },
    onDownloadPdfProductsReportClick() {
      this.$emit('download-pdf-products-report')
    },
    onSendEmailProductsReportClick() {
      this.$emit('send-email-products-report')
    },
    onHistoryProductsReportClick() {
      this.$emit('history-products-report')
      this.$router.push({name: 'ReportsNotify', params: {filterReportType: null}})
    }
  }
}
</script>

<style scoped>

</style>
